/**
 * ConfigScreen — cuenta, plan, preferencias, datos, acerca de.
 *
 * Sin cambios de tier en producto (cambiar plan = mailto a Nilabs por ahora).
 * Eliminar cuenta = mailto (no implementamos delete real hasta tener flujo
 * GDPR + revocación de share-links).
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../lib/toast";
import { Icon } from "../lib/Icon";
import { getUsage, type UsageSummary } from "../api/auth";
import { listPlans } from "../api/plans";
import {
  cancelSubscription,
  getMySubscription,
  type CancelFeedback,
  type SubscriptionDisplay,
  type SubscriptionTier,
} from "../api/subscriptions";
import { CheckoutModal } from "./CheckoutModal";
import { redeemCoupon } from "../api/coupons";
import { CancellationSurveyModal } from "./CancellationSurveyModal";
import type { Route } from "../types";

// TIER_INFO: solo se usa para el card visual del upgrade. Los precios reales
// vienen del backend (`getMySubscription().price_cop_monthly`). El campo `price`
// acá es solo display — TODO mover a fetch del backend en próxima iteración.
const TIER_INFO: Record<SubscriptionTier, { label: string; price: number; clients: string }> = {
  solo: { label: "Solo", price: 49_000, clients: "15 clientes" },
  studio: { label: "Studio", price: 89_000, clients: "50 clientes" },
  unlimited: { label: "Unlimited", price: 149_000, clients: "Clientes ilimitados" },
};

interface ConfigScreenProps {
  go: (r: Route, query?: string) => void;
}

const APP_VERSION = "v0.1.0";
const APP_YEAR = "2026";

export function ConfigScreen({ go }: ConfigScreenProps) {
  const { user, logout } = useAuth();
  const toast = useToast();
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [watermarkPref, setWatermarkPref] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionDisplay | null>(null);
  const [checkoutTier, setCheckoutTier] = useState<SubscriptionTier | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelSurvey, setShowCancelSurvey] = useState(false);

  /** Carga usage + subscription. Reutilizable para refetch on focus / on demand. */
  const loadPlan = useCallback(async () => {
    try {
      const [u, sub] = await Promise.all([
        getUsage(),
        getMySubscription().catch(() => null),
      ]);
      setUsage(u);
      setWatermarkPref(u.watermark);
      setSubscription(sub);
    } catch {
      // silent — config sigue siendo usable sin usage
    } finally {
      setLoadingUsage(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!cancelled) loadPlan();

    // Refresh al volver el foco a la ventana (típico cuando el coach paga en MP
    // y vuelve al tab; sin esto se quedaba con el plan cacheado anterior).
    const onFocus = () => { if (!cancelled) loadPlan(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [loadPlan]);

  /** Confirma la cancelación desde el modal de exit survey (feedback opcional). */
  const handleCancelConfirm = useCallback(async (feedback?: CancelFeedback) => {
    if (!subscription || !subscription.can_cancel) return;
    const isPending = subscription.status === "pending";
    setCancelling(true);
    try {
      await cancelSubscription(feedback);
      const sub = await getMySubscription();
      setSubscription(sub);
      setShowCancelSurvey(false);
      toast(
        isPending
          ? "Suscripción pendiente cancelada."
          : "Suscripción cancelada. Mantenés acceso hasta el final del periodo.",
      );
    } catch (e) {
      toast("No pude cancelar: " + (e instanceof Error ? e.message : ""));
    } finally {
      setCancelling(false);
    }
  }, [subscription, toast]);

  const onExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const plans = await listPlans();
      const blob = new Blob([JSON.stringify(plans, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `aetherlite-planes-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast(`Exportados ${plans.length} planes`);
    } catch (e) {
      toast("No pude exportar: " + (e instanceof Error ? e.message : ""));
    } finally {
      setExporting(false);
    }
  }, [exporting, toast]);

  const watermarkLocked = usage ? usage.plan_tier === "free" : true;

  return (
    <div className="page al-config">
      <div className="al-config-head">
        <div>
          <h1 className="al-page-title">Configuración</h1>
          <p className="muted">Cuenta, plan, preferencias y datos.</p>
        </div>
      </div>

      {/* ---------- Cuenta ---------- */}
      <Section title="Cuenta">
        <Row label="Email">
          <span className="mono">{user?.email}</span>
        </Row>
        <Row label="Nombre">
          <span>{user?.name || "—"}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => go("brand")}>
            Editar en Mi marca <Icon name="arrow" size={12} />
          </button>
        </Row>
        <div className="al-config-row-end">
          <button className="btn btn-secondary" onClick={logout}>
            <Icon name="logout" size={14} /> Cerrar sesión
          </button>
        </div>
      </Section>

      {/* ---------- Plan ---------- */}
      <Section title="Plan actual">
        {loadingUsage ? (
          <div className="muted tiny">Cargando…</div>
        ) : usage ? (
          <>
            <Row label="Plan">
              <span><b>{usage.plan_label}</b></span>
              {usage.price_cop_monthly > 0 ? (
                <span className="muted">${usage.price_cop_monthly.toLocaleString("es-CO")} COP / mes</span>
              ) : (
                <span className="chip">Gratis</span>
              )}
            </Row>
            <Row label="Clientes activos">
              <span>
                {usage.clients_active} / {usage.is_unlimited || usage.clients_limit < 0 ? "∞ Ilimitado" : usage.clients_limit}
              </span>
            </Row>

            {/* Estado de suscripción MP (si tiene una) */}
            {subscription && subscription.status !== "none" && (
              <>
                <Row label="Estado">
                  <SubStatusBadge status={subscription.status} />
                  {subscription.mode && (
                    <span className="muted tiny">
                      {subscription.mode === "auto" ? "Cobro automático mensual" : "Pago manual mensual"}
                    </span>
                  )}
                </Row>
                {subscription.renews_at && (
                  <Row label={subscription.status === "cancelled" ? "Acceso hasta" : "Próxima renovación"}>
                    <span>{new Date(subscription.renews_at).toLocaleDateString("es-CO", {
                      day: "numeric", month: "long", year: "numeric",
                    })}</span>
                  </Row>
                )}
              </>
            )}

            {/* Acciones: cancelar siempre que can_cancel (active o pending) */}
            {subscription?.can_cancel && (
              <div className="al-config-row-end" style={{ marginTop: 12 }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowCancelSurvey(true)}
                  disabled={cancelling}
                  style={{ borderColor: "#dc2626", color: "#dc2626" }}
                >
                  <Icon name="x" size={14} />{" "}
                  {subscription.status === "pending"
                    ? "Cancelar suscripción pendiente"
                    : "Cancelar suscripción recurrente"}
                </button>
              </div>
            )}

            {/* Upgrade cards: solo cuando NO está pendiente ni cancelable */}
            {!subscription?.can_cancel && (
              <div style={{ marginTop: 16 }}>
                <div className="al-pb-side-eyebrow" style={{ marginBottom: 10 }}>
                  CAMBIAR DE PLAN
                </div>
                {usage.plan_tier === "free" && (
                  <div className="tiny muted" style={{ marginBottom: 10 }}>
                    Free incluye app móvil para tus clientes. Los planes pagos suman generación con IA y quitan la marca de agua.
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                  {(["solo", "studio", "unlimited"] as SubscriptionTier[]).map((t) => {
                    const info = TIER_INFO[t];
                    const isCurrent = usage.plan_tier === t;
                    return (
                      <button
                        key={t}
                        className={"al-config-tier-card" + (isCurrent ? " is-current" : "")}
                        onClick={() => !isCurrent && setCheckoutTier(t)}
                        disabled={isCurrent}
                      >
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{info.label}</div>
                        <div style={{ fontWeight: 800, fontSize: 18, color: "var(--primary)", marginTop: 4 }}>
                          ${info.price.toLocaleString("es-CO")}
                          <span className="tiny muted" style={{ fontWeight: 400 }}> / mes</span>
                        </div>
                        <div className="tiny muted" style={{ marginTop: 4 }}>{info.clients}</div>
                        {isCurrent && (
                          <div className="tiny" style={{ marginTop: 6, color: "var(--primary)", fontWeight: 600 }}>
                            ✓ Plan actual
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="tiny muted" style={{ marginTop: 8, lineHeight: 1.4 }}>
                  Pago seguro vía MercadoPago. Cancelás cuando quieras.
                </div>
              </div>
            )}
            <RedeemCouponRow onDone={loadPlan} />
          </>
        ) : (
          <div className="muted tiny">No pudimos cargar tu plan.</div>
        )}
      </Section>

      {/* ---------- Banner Cross-sell a AetherCoach ----------
          Discreto: card que linkea al producto premium de la familia para
          coaches que ya están en Studio/Unlimited (señal de que están
          creciendo). NO interferimos con el flow de Lite. */}
      <Section title="¿Querés más?">
        <div className="al-coach-promo">
          <div className="al-coach-promo-hd">
            <span className="al-coach-promo-badge">PRO</span>
            <div>
              <div className="al-coach-promo-title">Conocé AetherCoach</div>
              <div className="al-coach-promo-sub">La plataforma de coaching completa de la familia Aether</div>
            </div>
          </div>
          <ul className="al-coach-promo-list">
            <li>📸 <b>Coach IA Pro</b> — análisis de fotos con Vision y form check de videos</li>
            <li>📊 <b>Antropometría profesional</b> — Heath-Carter, Kerr 5C, Faulkner</li>
            <li>🎮 <b>Gamificación</b> — XP, streaks, retos y leaderboards</li>
            <li>💬 <b>Chat en tiempo real</b> con tus clientes</li>
            <li>🏪 <b>Marketplace + cursos</b> integrados</li>
            <li>🏋️ <b>Modalidades</b> — Crossfit, Hyrox, calistenia</li>
          </ul>
          <a
            className="btn btn-secondary"
            href="https://aethercoach.nilabs.co"
            target="_blank"
            rel="noopener noreferrer"
          >
            Conocer AetherCoach →
          </a>
        </div>
      </Section>

      {/* ---------- Preferencias ---------- */}
      <Section title="Preferencias">
        <Row label="Marca de agua AetherLite">
          <label className="al-config-toggle">
            <input
              type="checkbox"
              checked={watermarkLocked ? true : watermarkPref}
              disabled={watermarkLocked}
              onChange={(e) => {
                setWatermarkPref(e.target.checked);
                toast("Próximamente: persistir esta preferencia");
              }}
            />
            <span>Mostrar "Hecho con AetherLite" en planes compartidos</span>
          </label>
          {watermarkLocked && (
            <span className="tiny muted">Disponible en plan Solo+. Cambia tu plan para ocultarla.</span>
          )}
        </Row>
      </Section>

      {/* ---------- Datos ---------- */}
      <Section title="Tus datos">
        <Row label="Exportar planes">
          <span className="muted tiny">Descarga un JSON con todos tus planes (incluye datos completos).</span>
          <button className="btn btn-secondary btn-sm" onClick={onExport} disabled={exporting}>
            <Icon name="upload" size={14} /> {exporting ? "Exportando…" : "Exportar (JSON)"}
          </button>
        </Row>
        <Row label="Eliminar cuenta">
          <span className="muted tiny">Esta acción borra tu usuario, todos tus planes y los share links.</span>
          <button className="btn btn-danger btn-sm" onClick={() => setShowDelete(true)}>
            Eliminar mi cuenta
          </button>
        </Row>
      </Section>

      {/* ---------- About ---------- */}
      <Section title="Acerca de">
        <Row label="Versión">
          <span className="mono">AetherLite {APP_VERSION} · Nilabs · {APP_YEAR}</span>
        </Row>
        <Row label="Legal">
          <a className="footer-link" href="#/terms" onClick={(e) => { e.preventDefault(); go("terms"); }}>
            Términos
          </a>
          <a className="footer-link" href="#/privacy" onClick={(e) => { e.preventDefault(); go("privacy"); }}>
            Privacidad
          </a>
          <a className="footer-link" href="mailto:contacto@nilabs.co">contacto@nilabs.co</a>
        </Row>
      </Section>

      {/* ---------- Checkout MercadoPago ---------- */}
      {checkoutTier && (
        <CheckoutModal
          tier={checkoutTier}
          tierLabel={TIER_INFO[checkoutTier].label}
          amountCop={TIER_INFO[checkoutTier].price}
          onClose={() => setCheckoutTier(null)}
        />
      )}

      {/* ---------- Exit survey al cancelar ---------- */}
      {showCancelSurvey && subscription && (
        <CancellationSurveyModal
          status={subscription.status === "pending" ? "pending" : "active"}
          renewsAt={subscription.renews_at}
          cancelling={cancelling}
          onConfirm={handleCancelConfirm}
          onClose={() => !cancelling && setShowCancelSurvey(false)}
        />
      )}

      {/* ---------- Modal eliminar ---------- */}
      {showDelete && (
        <div className="al-modal-backdrop" onClick={() => setShowDelete(false)}>
          <div className="al-modal" onClick={(e) => e.stopPropagation()}>
            <div className="card-header">
              <div className="card-title">Eliminar cuenta</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowDelete(false)}>
                <Icon name="x" size={14} />
              </button>
            </div>
            <p>
              Esta acción borra tu usuario, todos tus planes y los share links.
              Para confirmar, escríbenos a <b>contacto@nilabs.co</b> desde el correo
              asociado a tu cuenta y procesamos la baja en 24–48 hs.
            </p>
            <div className="al-modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowDelete(false)}>
                Cancelar
              </button>
              <a
                className="btn btn-danger"
                href={`mailto:contacto@nilabs.co?subject=Eliminar%20cuenta%20AetherLite&body=Hola%2C%20quiero%20dar%20de%20baja%20mi%20cuenta%20${encodeURIComponent(user?.email || "")}`}
              >
                Enviar email
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SubStatusBadge({ status }: { status: SubscriptionDisplay["status"] }) {
  const map: Record<SubscriptionDisplay["status"], { label: string; bg: string }> = {
    none: { label: "Sin suscripción", bg: "#94a3b8" },
    pending: { label: "Procesando", bg: "#f59e0b" },
    active: { label: "Activa", bg: "#10b981" },
    past_due: { label: "Pago vencido", bg: "#ef4444" },
    cancelled: { label: "Cancelada", bg: "#71717a" },
  };
  const info = map[status] || map.none;
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: 999,
      background: info.bg,
      color: "#fff",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.04em",
    }}>{info.label}</span>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card al-config-section">
      <div className="card-title">{title}</div>
      <div className="al-config-section-body">{children}</div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="al-config-row">
      <div className="al-config-row-label">{label}</div>
      <div className="al-config-row-content">{children}</div>
    </div>
  );
}

// ----------------------------------------------------------------------
// RedeemCouponRow — canjear un cupón de "meses gratis" (ej. influencer).
// ----------------------------------------------------------------------
function RedeemCouponRow({ onDone }: { onDone: () => void }) {
  const toast = useToast();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const c = code.trim();
    if (!c) return;
    setBusy(true);
    try {
      const res = await redeemCoupon(c);
      toast(`¡Listo! ${res.free_months} mes(es) gratis activados.`);
      setCode("");
      onDone();
    } catch (e) {
      toast("No pude canjear el cupón: " + (e instanceof Error ? e.message : ""));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div className="al-pb-side-eyebrow" style={{ marginBottom: 8 }}>CANJEAR CUPÓN</div>
      <div className="tiny muted" style={{ marginBottom: 8, lineHeight: 1.4 }}>
        ¿Tenés un código de meses gratis? Ingresalo y activamos tu plan al toque.
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          className="al-input"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Código del cupón"
          style={{ flex: 1, textTransform: "uppercase" }}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
        />
        <button className="btn btn-secondary" onClick={submit} disabled={busy || !code.trim()}>
          {busy ? "…" : "Canjear"}
        </button>
      </div>
    </div>
  );
}
